import emailjs from '@emailjs/browser';
import { ContactForm, Order, UserInfo } from '../types';
import { getEmailConfig } from '../config/siteConfig';

const emailConfig = getEmailConfig();

export const sendContactEmail = async (formData: ContactForm): Promise<boolean> => {
  try {
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      message: formData.message,
      to_email: emailConfig.contact,
      subject: `New Contact Form Submission from ${formData.name}`,
    };

    await emailjs.send(
      emailConfig.serviceId,
      emailConfig.templates.contact,
      templateParams,
      emailConfig.publicKey
    );

    return true;
  } catch (error) {
    console.error('Error sending contact email:', error);
    return false;
  }
};

export const sendOrderEmail = async (order: Order, userInfo: UserInfo): Promise<boolean> => {
  try {
    const orderDetails = order.items.map(item => 
      `${item.product.title} - Qty: ${item.quantity} - $${(item.product.price * item.quantity).toFixed(2)}`
    ).join('\n');

    const templateParams = {
      customer_name: userInfo.name,
      customer_email: userInfo.email,
      customer_address: userInfo.address,
      customer_phone: userInfo.phone || 'Not provided',
      order_id: order.id,
      order_date: order.createdAt.toLocaleDateString(),
      order_details: orderDetails,
      order_subtotal: `$${(order.total / 1.08).toFixed(2)}`,
      order_tax: `$${(order.total - (order.total / 1.08)).toFixed(2)}`,
      order_total: `$${order.total.toFixed(2)}`,
      order_notes: userInfo.notes || 'No additional notes',
      to_email: emailConfig.orders,
      subject: `New Order #${order.id} from ${userInfo.name}`,
    };

    await emailjs.send(
      emailConfig.serviceId,
      emailConfig.templates.order,
      templateParams,
      emailConfig.publicKey
    );

    return true;
  } catch (error) {
    console.error('Error sending order email:', error);
    return false;
  }
};

// Enhanced fallback function for development/testing
export const sendContactEmailFallback = async (formData: ContactForm): Promise<boolean> => {
  // Simulate email sending for development
  console.log('📧 Contact form submitted:', {
    from: formData.name,
    email: formData.email,
    message: formData.message,
    to: emailConfig.contact
  });
  await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
  return true;
};

export const sendOrderEmailFallback = async (order: Order, userInfo: UserInfo): Promise<boolean> => {
  // Simulate email sending for development
  console.log('📧 Order submitted:', {
    orderId: order.id,
    customer: userInfo.name,
    email: userInfo.email,
    total: `$${order.total.toFixed(2)}`,
    items: order.items.length,
    to: emailConfig.orders
  });
  
  // Log detailed order information
  console.log('📋 Order Details:', {
    items: order.items.map(item => ({
      product: item.product.title,
      quantity: item.quantity,
      price: `$${item.product.price.toFixed(2)}`,
      total: `$${(item.product.price * item.quantity).toFixed(2)}`
    })),
    customerInfo: userInfo,
    orderTotal: `$${order.total.toFixed(2)}`
  });
  
  await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
  return true;
}; 